import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { compose } from 'recompose';

import requestCountry from 'js/lib/requestCountry';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import user from 'js/lib/user';

import useIsInLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication-common/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import CredentialTrackEnrollModal from 'bundles/enroll-modal/components/credential-track/CredentialTrackEnrollModal';
import EoiWidgetContainer from 'bundles/expression-of-interest/components/xddp/EoiWidgetContainer';
import { ACTION_QUERY_PARAMS, ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonType } from 'bundles/expression-of-interest/types';
import { clearActionQueryParamLocation, getActionQueryParam } from 'bundles/expression-of-interest/utils/util';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import type { PropsFromWithCredentialTrackOwnableProducts } from 'bundles/ownable-products-common/components/xdp/withNaptimeData';
import { withCredentialTrackOwnableProducts } from 'bundles/ownable-products-common/components/xdp/withNaptimeData';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';
import type { EnrollmentFlowProps } from 'bundles/premium-certificate/types';
import { trackApplyClick, trackEmailMeInfoClick } from 'bundles/premium-certificate/utils';
import withPromotionInfo, { withPromoCodeFromUrl } from 'bundles/promotions/components/withPromotionInfo';

import _t from 'i18n!nls/premium-certificate';

type Props = EnrollmentFlowProps & {
  id?: string;
  promotionEligibilities?: PromotionEligibilitiesV1;
} & PropsFromWithCredentialTrackOwnableProducts;

const EnrollModalFlow = ({
  id,
  actionBarData,
  slug,
  expressionOfInterestContainer,
  ownableProducts,
  promotionEligibilities,
  certificateName,
  certificateType,
}: Props) => {
  const [showEnrollModal, setEnrollModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const isLegalConsolidationFlowAndHasTouQueryParam = useIsInLegalConsolidationFlowAndHasTouQueryParam();

  const credentialTrackProductSku = ownableProducts?.find(
    (sku) => sku.productType === ProductTypeObject.CREDENTIAL_TRACK
  );

  const credentialTrackSubscriptionProductSkus = useMemo(
    () => ownableProducts?.filter((sku) => sku.productType !== ProductTypeObject.CREDENTIAL_TRACK),
    [ownableProducts]
  );

  const handleError = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((e: ApiError) => {
    setError(e);
    setEnrollModal(false);
    setShowErrorModal(true);
  }, []);
  const onEnrollButtonClick = useCallback<() => $TSFixMe>(() => {
    trackApplyClick(slug, id, certificateName, certificateType);

    // We are enabling onetime payments for MTC in India.
    const countryCode = requestCountry.get();
    if (user.isAuthenticatedUser()) {
      if (credentialTrackProductSku && countryCode === 'IN') {
        const [, productItemId] = stringKeyToTuple(credentialTrackProductSku.id);
        const options = { credentialTrackProductId: productItemId, productType: credentialTrackProductSku.productType };
        const handleSubmitPromise =
          choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.ENROLL_THROUGH_CREDENTIALTRACK];
        const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined;

        submitEnrollmentPromise({
          handleSubmitPromise,
          options,
          promoCode,
        }).catch(handleError);
      } else {
        setEnrollModal(true);
      }
    }
  }, [
    slug,
    id,
    certificateName,
    certificateType,
    credentialTrackProductSku,
    promotionEligibilities?.isEligible,
    promotionEligibilities?.promoCodeId,
    handleError,
  ]);

  useEffect(() => {
    const actionQueryParam = getActionQueryParam(location);
    if (actionQueryParam === ACTION_QUERY_PARAMS.ENROLL && !isLegalConsolidationFlowAndHasTouQueryParam) {
      onEnrollButtonClick();
      clearActionQueryParamLocation(location, navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegalConsolidationFlowAndHasTouQueryParam]);

  let enrollButton: EoiButtonType | null = null;

  if (ownableProducts?.length || !user.isAuthenticatedUser()) {
    const enrollButtonText = expressionOfInterestContainer?.applyButtonText;

    enrollButton = {
      actionType: ACTION_TYPES.apply,
      doAction: () => onEnrollButtonClick(),
      buttonOptions: {
        label: enrollButtonText || _t('Enroll'),
        trackingName: 'enroll',
        trackingData: { section: 'headerBanner' },
      },
    };
  }
  const emailMeInfoButton: EoiButtonType = {
    actionType: ACTION_TYPES.emailMeInfo,
    doAction: () => trackEmailMeInfoClick(slug, id, certificateName, certificateType),
  };

  const handleClose = () => {
    setEnrollModal(false);
    const button: HTMLElement | null = document.querySelector('.cds-button-focusVisible');
    button?.focus();
  };

  return (
    <div data-test="EnrollModalFlow">
      {showEnrollModal && (
        <TopLevelModal>
          <CredentialTrackEnrollModal
            ownableProducts={credentialTrackSubscriptionProductSkus}
            onClose={handleClose}
            certificateType={certificateType}
          />
        </TopLevelModal>
      )}
      {showErrorModal && (
        <TopLevelModal>
          <EnrollErrorModal
            error={error}
            onClose={() => setShowErrorModal(false)}
            s12nId={credentialTrackProductSku ? stringKeyToTuple(credentialTrackProductSku.id)[2] : undefined}
          />
        </TopLevelModal>
      )}
      <EoiWidgetContainer
        slug={slug}
        degreeId={id || ''}
        actionBarData={actionBarData}
        applicationUrl={expressionOfInterestContainer?.applyButtonUrl || ''}
        programInterestText={expressionOfInterestContainer?.programInterestText || ''}
        enrollButtonData={enrollButton}
        emailMeInfoButtonData={emailMeInfoButton}
        isEnrollFlow
      />
    </div>
  );
};

export default compose<Props, EnrollmentFlowProps & { id?: string }>(
  withCredentialTrackOwnableProducts(),
  withPromoCodeFromUrl(),
  withPromotionInfo()
)(EnrollModalFlow);
